import { useState } from 'react';
import { ArrowLeft, Heart, Share2, MoreVertical, ChevronDown, Plus, Lock, Clock, Tag, Users, Edit2, MapPin, Check, Bookmark, MessageCircle, Eye, Search, Calendar, Filter, X } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';

interface Creator {
  id: string;
  name: string;
  handle: string;
  avatar: string;
  category: string;
  followers: string;
  engagementRate: string;
  platform: 'Instagram' | 'YouTube' | 'TikTok' | 'Multi';
  verified?: boolean;
  location?: string;
  recentContent?: Array<{ image: string; title: string }>;
}

interface CreatorList {
  id: string;
  name: string;
  description: string;
  coverImage: string;
  creatorCount: number;
  totalFollowers: string;
  avgEngagement: string;
  trackingEnabled: boolean;
  lastScanAt?: string;
  keywords: string[];
  platforms: string[];
  mentionsCount?: number;
  isPublic: boolean;
}

interface Mention {
  id: string;
  creator: {
    name: string;
    handle: string;
    avatar: string;
    verified: boolean;
  };
  platform: 'Instagram' | 'TikTok' | 'YouTube';
  postThumbnail: string;
  caption: string;
  keyword: string;
  postedAt: string;
  likes: number;
  comments: number;
  views: number;
}

interface LeaderboardEntry {
  rank: number;
  creator: {
    id: string;
    name: string;
    handle: string;
    avatar: string;
    verified: boolean;
  };
  followers: string;
  followerCount: number;
  mentionCount: number;
  totalEngagement: string;
  avgEngagement: string;
  recentPosts: number;
}

export function CreatorListDetailPage({
  listId: _listIdProp,
  onNavigate: _onNavigate,
}: {
  listId?: string;
  onNavigate?: (page: string) => void;
} = {}) {
  const [selectedTab, setSelectedTab] = useState<'creators' | 'mentions' | 'leaderboard' | 'settings'>('creators');
  const [sortBy, setSortBy] = useState<'followers' | 'engagement' | 'avgViews'>('followers');
  const [isLiked, setIsLiked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [timeframe, setTimeframe] = useState<'7days' | '14days' | '30days' | 'custom'>('7days');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['Instagram', 'TikTok', 'YouTube']);

  // Mock data
  const creatorList: CreatorList = {
    id: '1',
    name: 'Beauty Influencers 2024',
    description: 'Top beauty and skincare influencers to collaborate with for our spring campaign',
    coverImage: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=400&fit=crop',
    creatorCount: 25,
    totalFollowers: '1.2M',
    avgEngagement: '3.5%',
    trackingEnabled: true,
    lastScanAt: '2 hours ago',
    keywords: ['beauty', 'skincare', 'makeup'],
    platforms: ['Instagram', 'TikTok'],
    mentionsCount: 142,
    isPublic: true,
  };

  const creators: Creator[] = [
    {
      id: '1',
      name: 'Emma Rodriguez',
      handle: '@emmabeauty',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
      category: 'Beauty',
      followers: '234K',
      engagementRate: '4.2%',
      platform: 'Instagram',
      verified: true,
      location: 'Los Angeles, CA',
      recentContent: [
        { image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200&h=300&fit=crop', title: 'Post 1' },
        { image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=200&h=300&fit=crop', title: 'Post 2' },
        { image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&h=300&fit=crop', title: 'Post 3' },
        { image: 'https://images.unsplash.com/photo-1515688594390-b649af70d282?w=200&h=300&fit=crop', title: 'Post 4' },
        { image: 'https://images.unsplash.com/photo-1524502397800-2eeaad7c3fe5?w=200&h=300&fit=crop', title: 'Post 5' },
      ],
    },
    {
      id: '2',
      name: 'Sophia Chen',
      handle: '@sophiaskincare',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
      category: 'Skincare',
      followers: '189K',
      engagementRate: '5.1%',
      platform: 'Instagram',
      verified: true,
      location: 'New York, NY',
      recentContent: [
        { image: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=200&h=300&fit=crop', title: 'Post 1' },
        { image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=200&h=300&fit=crop', title: 'Post 2' },
        { image: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=200&h=300&fit=crop', title: 'Post 3' },
        { image: 'https://images.unsplash.com/photo-1590439471364-192aa70c0b53?w=200&h=300&fit=crop', title: 'Post 4' },
        { image: 'https://images.unsplash.com/photo-1526045478516-99145907023c?w=200&h=300&fit=crop', title: 'Post 5' },
      ],
    },
    {
      id: '3',
      name: 'Maya Johnson',
      handle: '@mayamakeup',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop',
      category: 'Makeup',
      followers: '312K',
      engagementRate: '3.8%',
      platform: 'Instagram',
      verified: true,
      location: 'Miami, FL',
      recentContent: [
        { image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=200&h=300&fit=crop', title: 'Post 1' },
        { image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=200&h=300&fit=crop', title: 'Post 2' },
        { image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=200&h=300&fit=crop', title: 'Post 3' },
        { image: 'https://images.unsplash.com/photo-1515688594390-b649af70d282?w=200&h=300&fit=crop', title: 'Post 4' },
        { image: 'https://images.unsplash.com/photo-1524502397800-2eeaad7c3fe5?w=200&h=300&fit=crop', title: 'Post 5' },
      ],
    },
  ];

  const mentions: Mention[] = [
    {
      id: '1',
      creator: {
        name: 'Emma Rodriguez',
        handle: '@emmabeauty',
        avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
        verified: true,
      },
      platform: 'Instagram',
      postThumbnail: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=300&h=400&fit=crop',
      caption: 'Obsessed with this new skincare routine! The results have been amazing 💕',
      keyword: 'skincare',
      postedAt: '2 hours ago',
      likes: 15234,
      comments: 892,
      views: 45678,
    },
    {
      id: '2',
      creator: {
        name: 'Sophia Chen',
        handle: '@sophiaskincare',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
        verified: true,
      },
      platform: 'TikTok',
      postThumbnail: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=300&h=400&fit=crop',
      caption: 'My go-to makeup look for summer ☀️',
      keyword: 'makeup',
      postedAt: '5 hours ago',
      likes: 23456,
      comments: 1203,
      views: 67890,
    },
    {
      id: '3',
      creator: {
        name: 'Maya Johnson',
        handle: '@mayamakeup',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop',
        verified: true,
      },
      platform: 'Instagram',
      postThumbnail: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=300&h=400&fit=crop',
      caption: 'Testing out the latest beauty trends! What do you think?',
      keyword: 'beauty',
      postedAt: '1 day ago',
      likes: 18901,
      comments: 756,
      views: 52340,
    },
  ];

  const leaderboard: LeaderboardEntry[] = [
    {
      rank: 1,
      creator: {
        id: '3',
        name: 'Maya Johnson',
        handle: '@mayamakeup',
        avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop',
        verified: true,
      },
      followers: '312K',
      followerCount: 312000,
      mentionCount: 48,
      totalEngagement: '156K',
      avgEngagement: '5.2%',
      recentPosts: 24,
    },
    {
      rank: 2,
      creator: {
        id: '1',
        name: 'Emma Rodriguez',
        handle: '@emmabeauty',
        avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
        verified: true,
      },
      followers: '234K',
      followerCount: 234000,
      mentionCount: 42,
      totalEngagement: '142K',
      avgEngagement: '4.8%',
      recentPosts: 31,
    },
    {
      rank: 3,
      creator: {
        id: '2',
        name: 'Sophia Chen',
        handle: '@sophiaskincare',
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
        verified: true,
      },
      followers: '189K',
      followerCount: 189000,
      mentionCount: 38,
      totalEngagement: '128K',
      avgEngagement: '4.5%',
      recentPosts: 27,
    },
  ];

  return (
    <div className="mainContainer">
      {/* Main Content */}
      <div className="mainContent">

        {/* Back Button Container */}
        <div className="backButtonContainer">
          <button className="backButton">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Lists</span>
          </button>
        </div>

        {/* Header Section */}
        <div className="header">
          <div className="headerGradient" />

          <div className="headerContent">
            <div className="coverImage">
              <ImageWithFallback
                src={creatorList.coverImage}
                alt={creatorList.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button className="editPictureOverlay">Edit</button>
            </div>

            <div className="headerText">
              <div className="typeLabel">CREATOR LIST</div>

              <h1 className="title">
                {creatorList.name}
                <button className="editNameButton">
                  <Edit2 className="w-5 h-5" />
                </button>
              </h1>

              <div className="permissionsLabel">
                Owner - {creatorList.isPublic ? 'Public' : 'Private'}
              </div>

              <p className="description">
                {creatorList.description}
              </p>

              <div className="headerActions">
                <button
                  className={`heartButton ${isLiked ? 'active' : ''}`}
                  onClick={() => setIsLiked(!isLiked)}
                >
                  <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                </button>
                <button className="shareButton">
                  <Share2 className="w-5 h-5" />
                </button>
                <button className="addCreatorsButton">
                  <Plus className="w-5 h-5" />
                </button>
                <button className="moreButton">
                  <MoreVertical className="w-5 h-5" />
                </button>
              </div>

              <div className="headerStats">
                <div className="stat">
                  <span className="statValue">{creatorList.totalFollowers}</span>
                  <span className="statLabel">Total Followers</span>
                </div>
                <div className="stat">
                  <span className="statValue">{creatorList.avgEngagement}</span>
                  <span className="statLabel">Avg Engagement</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="contentArea">
          <div className="contentAreaInner">

            {/* Tracking Row */}
            {creatorList.trackingEnabled && (
              <div className="trackingRow">
                <div className="trackingInfo">
                  <span className="trackingBadge trackingBadgeActive">
                    <span className="activeDot" />
                    Tracking Active
                  </span>
                  <span className="trackingInfoItem">
                    <Clock className="trackingIcon w-4 h-4" />
                    Last scan: {creatorList.lastScanAt}
                  </span>
                  <span className="trackingInfoItem">
                    <Tag className="trackingIcon w-4 h-4" />
                    Keywords: {creatorList.keywords.join(', ')}
                  </span>
                  <span className="trackingInfoItem">
                    <Users className="trackingIcon w-4 h-4" />
                    Platforms: {creatorList.platforms.join(', ')}
                  </span>
                </div>
                <div className="trackingActions">
                  <button
                    className="manageKeywordsButton"
                    onClick={() => setSelectedTab('settings')}
                  >
                    Manage Keywords
                  </button>
                  <button className="rescanButton">
                    Rescan
                  </button>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="tabNavigation">
              <div className="tabNavigationLeft">
                <button
                  className={`tab ${selectedTab === 'creators' ? 'activeTab' : ''}`}
                  onClick={() => setSelectedTab('creators')}
                >
                  Creators ({creatorList.creatorCount})
                </button>
                {creatorList.trackingEnabled ? (
                  <>
                    <button
                      className={`tab ${selectedTab === 'mentions' ? 'activeTab' : ''}`}
                      onClick={() => setSelectedTab('mentions')}
                    >
                      Mentions
                      {creatorList.mentionsCount && creatorList.mentionsCount > 0 && (
                        <span className="tabBadge">{creatorList.mentionsCount}</span>
                      )}
                    </button>
                    <button
                      className={`tab ${selectedTab === 'leaderboard' ? 'activeTab' : ''}`}
                      onClick={() => setSelectedTab('leaderboard')}
                    >
                      Leaderboard
                    </button>
                    <button
                      className={`tab ${selectedTab === 'settings' ? 'activeTab' : ''}`}
                      onClick={() => setSelectedTab('settings')}
                    >
                      Settings
                    </button>
                  </>
                ) : (
                  <>
                    <button className="tab disabledTab" disabled>
                      <Lock className="w-4 h-4" />
                      Mentions
                    </button>
                    <button className="tab disabledTab" disabled>
                      <Lock className="w-4 h-4" />
                      Leaderboard
                    </button>
                  </>
                )}
              </div>

              <div className="tabNavigationRight">
                <div className="sortButtons">
                  <button
                    className={`sortButton ${sortBy === 'followers' ? 'active' : ''}`}
                    onClick={() => setSortBy('followers')}
                  >
                    Followers
                  </button>
                  <button
                    className={`sortButton ${sortBy === 'engagement' ? 'active' : ''}`}
                    onClick={() => setSortBy('engagement')}
                  >
                    Engagement
                  </button>
                  <button
                    className={`sortButton ${sortBy === 'avgViews' ? 'active' : ''}`}
                    onClick={() => setSortBy('avgViews')}
                  >
                    Avg Views
                  </button>
                </div>
              </div>
            </div>

            {/* Results List */}
            {selectedTab === 'creators' && (
              <div className="mentionsContainer">
                {/* Search Bar */}
                <div className="mentionsToolbar">
                  <div className="mentionsSearchBar">
                    <div className="searchInputWrapper">
                      <Search className="searchInputIcon" />
                      <input
                        type="text"
                        placeholder="Search creators..."
                        className="mentionsSearchInput"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mt-6">
                {creators
                  .filter(creator => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      creator.name.toLowerCase().includes(query) ||
                      creator.handle.toLowerCase().includes(query) ||
                      creator.category.toLowerCase().includes(query)
                    );
                  })
                  .map((creator) => (
                  <div
                    key={creator.id}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="px-6 py-3 flex gap-6 items-stretch">
                      {/* Left Side - Creator Info */}
                      <div className="flex-shrink-0 flex items-start">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-gray-200 cursor-pointer hover:border-purple-500 transition-colors">
                          <ImageWithFallback
                            src={creator.avatar}
                            alt={creator.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>

                      {/* Middle - Details */}
                      <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                        <div>
                          {/* Name and Actions */}
                          <div className="flex items-start justify-between mb-1.5">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="text-lg text-gray-900 cursor-pointer hover:text-purple-600 transition-colors">
                                  {creator.name}
                                </h3>
                                {creator.verified && (
                                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                                  </div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mb-1.5">{creator.handle}</p>
                              <div className="flex items-center gap-2 mb-2">
                                {creator.location && (
                                  <>
                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                      <MapPin className="w-3 h-3" />
                                      <span>{creator.location}</span>
                                    </div>
                                    <span className="text-gray-300">•</span>
                                  </>
                                )}
                                <div className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                                  {creator.category}
                                </div>
                              </div>
                            </div>

                            {/* Action Icons */}
                            <div className="flex items-center gap-1.5 ml-4">
                              <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                                <Heart className="w-4.5 h-4.5 text-gray-600" />
                              </button>
                              <button className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                                <Bookmark className="w-4.5 h-4.5 text-gray-600" />
                              </button>
                            </div>
                          </div>

                          {/* Platform */}
                          <div className="flex items-center gap-2 mb-2.5">
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-md text-gray-700">
                              <span className="text-xs">{creator.platform}</span>
                            </div>
                          </div>
                        </div>

                        {/* Stats and Expand Button */}
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-6">
                            <div>
                              <span className="text-gray-900">{creator.followers}</span>
                              <span className="text-gray-500 ml-1">Followers</span>
                            </div>
                            <div>
                              <span className="text-gray-900">{creator.engagementRate}</span>
                              <span className="text-gray-500 ml-1">Engagement</span>
                            </div>
                          </div>
                          <button className="flex items-center gap-1 px-3 py-1 text-xs text-purple-600 hover:bg-purple-50 rounded-full transition-colors">
                            <span>View Details</span>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Right Side - Content Thumbnails */}
                      <div className="flex-shrink-0 flex items-center">
                        <div className="flex gap-2">
                          {creator.recentContent && creator.recentContent.slice(0, 5).map((content, index) => (
                            <div
                              key={index}
                              className="w-36 h-52 rounded-lg overflow-hidden border border-gray-200 hover:border-purple-500 transition-colors cursor-pointer"
                            >
                              <ImageWithFallback
                                src={content.image}
                                alt={`Content ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}

            {/* Other Tab Content */}
            {selectedTab === 'mentions' && (
              <div className="mentionsContainer">
                {/* Search and Filter Bar */}
                <div className="mentionsToolbar">
                  <div className="mentionsSearchBar">
                    <div className="searchInputWrapper">
                      <Search className="searchInputIcon" />
                      <input
                        type="text"
                        placeholder="Search mentions..."
                        className="mentionsSearchInput"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Filter Button */}
                  <div className="filterButtonWrapper">
                    <button
                      className="filterButton"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <Filter className="filterIcon" />
                      <span>Filters</span>
                      {(selectedPlatforms.length < 3 || timeframe !== '7days') && (
                        <span className="filterBadge">{selectedPlatforms.length < 3 ? selectedPlatforms.length : '1'}</span>
                      )}
                    </button>

                    {showFilterDropdown && (
                      <div className="filterDropdown">
                        <div className="filterDropdownHeader">
                          <span className="filterDropdownTitle">Filters</span>
                          <button
                            className="filterCloseButton"
                            onClick={() => setShowFilterDropdown(false)}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Platform Filters */}
                        <div className="filterSection">
                          <div className="filterSectionTitle">Platform</div>
                          <div className="filterPlatformButtons">
                            {['Instagram', 'TikTok', 'YouTube'].map((platform) => (
                              <button
                                key={platform}
                                className={`filterPlatformButton ${selectedPlatforms.includes(platform) ? 'active' : ''}`}
                                onClick={() => {
                                  if (selectedPlatforms.includes(platform)) {
                                    setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
                                  } else {
                                    setSelectedPlatforms([...selectedPlatforms, platform]);
                                  }
                                }}
                              >
                                {platform}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Timeframe Filter */}
                        <div className="filterSection">
                          <div className="filterSectionTitle">Timeframe</div>
                          <div className="filterTimeframeButtons">
                            <button
                              className={`filterTimeframeButton ${timeframe === '7days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('7days');
                                setShowDatePicker(false);
                              }}
                            >
                              7 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === '14days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('14days');
                                setShowDatePicker(false);
                              }}
                            >
                              14 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === '30days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('30days');
                                setShowDatePicker(false);
                              }}
                            >
                              30 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === 'custom' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('custom');
                                setShowDatePicker(!showDatePicker);
                              }}
                            >
                              <Calendar className="filterTimeframeIcon" />
                              Custom Range
                            </button>
                          </div>

                          {showDatePicker && timeframe === 'custom' && (
                            <div className="filterDateRange">
                              <div className="filterDateInputGroup">
                                <label className="filterDateLabel">Start Date</label>
                                <input
                                  type="date"
                                  className="filterDateInput"
                                  value={customDateRange.start}
                                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                                />
                              </div>
                              <div className="filterDateInputGroup">
                                <label className="filterDateLabel">End Date</label>
                                <input
                                  type="date"
                                  className="filterDateInput"
                                  value={customDateRange.end}
                                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Filter Actions */}
                        <div className="filterActions">
                          <button
                            className="filterClearButton"
                            onClick={() => {
                              setSelectedPlatforms(['Instagram', 'TikTok', 'YouTube']);
                              setTimeframe('7days');
                              setShowDatePicker(false);
                              setCustomDateRange({ start: '', end: '' });
                            }}
                          >
                            Clear All
                          </button>
                          <button
                            className="filterApplyButton"
                            onClick={() => setShowFilterDropdown(false)}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Mentions Table */}
                <div className="mentionsTableWrapper">
                  <table className="mentionsTable">
                    <thead>
                      <tr className="mentionsTableHeader">
                        <th className="mentionsHeaderCell" style={{ width: '40px' }}>#</th>
                        <th className="mentionsHeaderCell" style={{ width: '200px' }}>Creator</th>
                        <th className="mentionsHeaderCell" style={{ width: 'auto' }}>Post Preview</th>
                        <th className="mentionsHeaderCell" style={{ width: '100px' }}>Platform</th>
                        <th className="mentionsHeaderCell" style={{ width: '120px' }}>Engagement</th>
                        <th className="mentionsHeaderCell" style={{ width: '100px' }}>Posted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mentions
                        .filter(mention => {
                          if (!searchQuery) return true;
                          const query = searchQuery.toLowerCase();
                          return (
                            mention.creator.name.toLowerCase().includes(query) ||
                            mention.creator.handle.toLowerCase().includes(query) ||
                            mention.caption.toLowerCase().includes(query)
                          );
                        })
                        .map((mention, index) => (
                        <tr key={mention.id} className="mentionsTableRow">
                          <td className="mentionsTableCell mentionsIndexCell">{index + 1}</td>
                          <td className="mentionsTableCell mentionsCreatorCell">
                            <div className="mentionsCreatorContent">
                              <ImageWithFallback
                                src={mention.creator.avatar}
                                alt={mention.creator.name}
                                className="mentionsCreatorAvatarTable"
                              />
                              <div className="mentionsCreatorInfo">
                                <div className="mentionsCreatorNameTable">
                                  {mention.creator.name}
                                  {mention.creator.verified && (
                                    <div className="verifiedBadge">
                                      <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                    </div>
                                  )}
                                </div>
                                <div className="mentionsCreatorHandleTable">{mention.creator.handle}</div>
                              </div>
                            </div>
                          </td>
                          <td className="mentionsTableCell mentionsPostCell">
                            <div className="mentionsPostContent">
                              <div className="mentionsPostThumbnail">
                                <ImageWithFallback
                                  src={mention.postThumbnail}
                                  alt="Post"
                                  className="mentionsPostImage"
                                />
                              </div>
                              <div className="mentionsPostCaption">
                                {mention.caption.split(new RegExp(`(${mention.keyword})`, 'gi')).map((part, i) =>
                                  part.toLowerCase() === mention.keyword.toLowerCase() ? (
                                    <span key={i} className="mentionsKeywordHighlight">#{part}</span>
                                  ) : (
                                    <span key={i}>{part}</span>
                                  )
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="mentionsTableCell mentionsPlatformCell">
                            <div className="mentionsPlatformBadgeTable">
                              {mention.platform === 'Instagram' && (
                                <>
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                  </svg>
                                  Instagram
                                </>
                              )}
                              {mention.platform === 'TikTok' && (
                                <>
                                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                                  </svg>
                                  TikTok
                                </>
                              )}
                            </div>
                          </td>
                          <td className="mentionsTableCell mentionsEngagementCell">
                            <div className="mentionsEngagementStats">
                              <div className="mentionsEngagementItem">
                                <Eye className="w-3.5 h-3.5" />
                                <span>{(mention.views / 1000).toFixed(1)}K</span>
                              </div>
                              <div className="mentionsEngagementItem">
                                <Heart className="w-3.5 h-3.5" />
                                <span>{(mention.likes / 1000).toFixed(1)}K</span>
                              </div>
                              <div className="mentionsEngagementItem">
                                <MessageCircle className="w-3.5 h-3.5" />
                                <span>{mention.comments}</span>
                              </div>
                            </div>
                          </td>
                          <td className="mentionsTableCell mentionsTimeCell">
                            {mention.postedAt}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedTab === 'leaderboard' && (
              <div className="leaderboardContainer">
                {/* Search Bar */}
                <div className="mentionsToolbar">
                  <div className="mentionsSearchBar">
                    <div className="searchInputWrapper">
                      <Search className="searchInputIcon" />
                      <input
                        type="text"
                        placeholder="Search leaderboard..."
                        className="mentionsSearchInput"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Filter Button */}
                  <div className="filterButtonWrapper">
                    <button
                      className="filterButton"
                      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    >
                      <Filter className="filterIcon" />
                      <span>Filters</span>
                      {(selectedPlatforms.length < 3 || timeframe !== '7days') && (
                        <span className="filterBadge">{selectedPlatforms.length < 3 ? selectedPlatforms.length + (timeframe !== '7days' ? 1 : 0) : timeframe !== '7days' ? '1' : ''}</span>
                      )}
                    </button>

                    {showFilterDropdown && (
                      <div className="filterDropdown">
                        <div className="filterDropdownHeader">
                          <span className="filterDropdownTitle">Filters</span>
                          <button
                            className="filterCloseButton"
                            onClick={() => setShowFilterDropdown(false)}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Platform Filters */}
                        <div className="filterSection">
                          <div className="filterSectionTitle">Platform</div>
                          <div className="filterPlatformButtons">
                            {['Instagram', 'TikTok', 'YouTube'].map((platform) => (
                              <button
                                key={platform}
                                className={`filterPlatformButton ${selectedPlatforms.includes(platform) ? 'active' : ''}`}
                                onClick={() => {
                                  if (selectedPlatforms.includes(platform)) {
                                    setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
                                  } else {
                                    setSelectedPlatforms([...selectedPlatforms, platform]);
                                  }
                                }}
                              >
                                {platform}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Timeframe Filter */}
                        <div className="filterSection">
                          <div className="filterSectionTitle">Timeframe</div>
                          <div className="filterTimeframeButtons">
                            <button
                              className={`filterTimeframeButton ${timeframe === '7days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('7days');
                                setShowDatePicker(false);
                              }}
                            >
                              7 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === '14days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('14days');
                                setShowDatePicker(false);
                              }}
                            >
                              14 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === '30days' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('30days');
                                setShowDatePicker(false);
                              }}
                            >
                              30 days
                            </button>
                            <button
                              className={`filterTimeframeButton ${timeframe === 'custom' ? 'active' : ''}`}
                              onClick={() => {
                                setTimeframe('custom');
                                setShowDatePicker(!showDatePicker);
                              }}
                            >
                              <Calendar className="filterTimeframeIcon" />
                              Custom Range
                            </button>
                          </div>

                          {showDatePicker && timeframe === 'custom' && (
                            <div className="filterDateRange">
                              <div className="filterDateInputGroup">
                                <label className="filterDateLabel">Start Date</label>
                                <input
                                  type="date"
                                  className="filterDateInput"
                                  value={customDateRange.start}
                                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                                />
                              </div>
                              <div className="filterDateInputGroup">
                                <label className="filterDateLabel">End Date</label>
                                <input
                                  type="date"
                                  className="filterDateInput"
                                  value={customDateRange.end}
                                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Filter Actions */}
                        <div className="filterActions">
                          <button
                            className="filterClearButton"
                            onClick={() => {
                              setSelectedPlatforms(['Instagram', 'TikTok', 'YouTube']);
                              setTimeframe('7days');
                              setShowDatePicker(false);
                              setCustomDateRange({ start: '', end: '' });
                            }}
                          >
                            Clear All
                          </button>
                          <button
                            className="filterApplyButton"
                            onClick={() => setShowFilterDropdown(false)}
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="leaderboardTableWrapper">
                  <table className="leaderboardTable">
                    <thead>
                      <tr className="leaderboardTableHeader">
                        <th className="leaderboardHeaderCell" style={{ width: '50px' }}>#</th>
                        <th className="leaderboardHeaderCell" style={{ width: '250px' }}>Creator</th>
                        <th className="leaderboardHeaderCell" style={{ width: '120px' }}>Channel</th>
                        <th className="leaderboardHeaderCell" style={{ width: '120px' }}>Followers</th>
                        <th className="leaderboardHeaderCell" style={{ width: '140px' }}>Mentions</th>
                        <th className="leaderboardHeaderCell" style={{ width: '100px' }}>Likes</th>
                        <th className="leaderboardHeaderCell" style={{ width: '100px' }}>Comments</th>
                        <th className="leaderboardHeaderCell" style={{ width: '100px' }}>Views</th>
                        <th className="leaderboardHeaderCell" style={{ width: '140px' }}>Latest Mention</th>
                        <th className="leaderboardHeaderCell" style={{ width: '80px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard
                        .filter(entry => {
                          if (!searchQuery) return true;
                          const query = searchQuery.toLowerCase();
                          return (
                            entry.creator.name.toLowerCase().includes(query) ||
                            entry.creator.handle.toLowerCase().includes(query)
                          );
                        })
                        .map((entry) => (
                        <tr key={entry.creator.id} className="leaderboardTableRow">
                          <td className="leaderboardTableCell leaderboardRankCell">{entry.rank}</td>
                          <td className="leaderboardTableCell leaderboardCreatorCellTable">
                            <div className="leaderboardCreatorContent">
                              <ImageWithFallback
                                src={entry.creator.avatar}
                                alt={entry.creator.name}
                                className="leaderboardAvatarTable"
                              />
                              <div className="leaderboardCreatorInfoTable">
                                <div className="leaderboardCreatorNameTable">
                                  {entry.creator.name}
                                  {entry.creator.verified && (
                                    <div className="verifiedBadge">
                                      <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                    </div>
                                  )}
                                </div>
                                <div className="leaderboardCreatorHandleTable">{entry.creator.handle}</div>
                              </div>
                            </div>
                          </td>
                          <td className="leaderboardTableCell leaderboardChannelCell">
                            <div className="leaderboardChannelBadge">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                              </svg>
                              INSTAGRAM
                            </div>
                          </td>
                          <td className="leaderboardTableCell leaderboardFollowersCell">{entry.followers}</td>
                          <td className="leaderboardTableCell leaderboardMentionsCell">
                            <div className="leaderboardMentionsContent">
                              <span className="leaderboardMentionsLink">{entry.mentionCount} mentions</span>
                              <span className="leaderboardMentionsSubtext">{entry.recentPosts} posts analyzed</span>
                            </div>
                          </td>
                          <td className="leaderboardTableCell leaderboardMetricCell">{entry.totalEngagement}</td>
                          <td className="leaderboardTableCell leaderboardMetricCell">{(parseFloat(entry.totalEngagement.replace('K', '')) * 0.3).toFixed(1)}K</td>
                          <td className="leaderboardTableCell leaderboardMetricCell">{(entry.followerCount / 1000).toFixed(1)}K</td>
                          <td className="leaderboardTableCell leaderboardLatestMentionCell">
                            {entry.rank === 1 ? 'Today' : entry.rank === 2 ? '3 weeks ago' : '2 weeks ago'}
                          </td>
                          <td className="leaderboardTableCell leaderboardActionsCell">
                            <button className="leaderboardActionsButton">
                              <MoreVertical className="w-5 h-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {selectedTab === 'settings' && (
              <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>Settings</h3>
                <p>Manage tracking keywords and settings</p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* CSS */}
      <style>{`
        /* ===== MAIN CONTAINER ===== */
        .mainContainer {
          height: 100vh;
          display: flex;
          background: #f8f9fa;
          color: #1a1a1a;
          font-family: 'Inter', sans-serif;
          overflow-x: auto;
          overflow-y: hidden;
        }

        .mainContent {
          flex: 1;
          background: #f8f9fa;
          position: relative;
          overflow: auto;
          display: flex;
          flex-direction: column;
        }

        /* ===== BACK BUTTON ===== */
        .backButtonContainer {
          padding: 16px 24px;
          background: #f8f9fa;
          border-bottom: none;
        }

        .backButton {
          display: flex;
          align-items: center;
          gap: 8px;
          background: none;
          border: none;
          color: #6b7280;
          font-size: 14px;
          cursor: pointer;
          font-weight: 400;
          line-height: 20px;
        }

        .backButton:hover {
          color: #4a5565;
        }

        /* ===== HEADER SECTION ===== */
        .header {
          position: relative;
          min-height: 340px;
          background: #f8f9fa;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 32px 24px 32px;
        }

        .headerGradient {
          display: none;
        }

        .headerContent {
          position: relative;
          z-index: 2;
          width: 100%;
          max-width: 1270px;
          display: flex;
          align-items: flex-start;
          gap: 32px;
        }

        .coverImage {
          width: 256px;
          height: 256px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
          flex-shrink: 0;
          position: relative;
          background: #ffffff;
        }

        .editPictureOverlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s ease;
          cursor: pointer;
          border: none;
          color: white;
          font-size: 14px;
          font-weight: 500;
        }

        .coverImage:hover .editPictureOverlay {
          opacity: 1;
        }

        .headerText {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0;
          justify-content: flex-start;
          padding-top: 0;
        }

        .typeLabel {
          font-size: 12px;
          font-weight: 400;
          color: #6b7280;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          line-height: 16px;
        }

        .permissionsLabel {
          font-size: 12px;
          font-weight: 400;
          color: #6b7280;
          margin-top: 4px;
          margin-bottom: 20px;
          line-height: 16px;
        }

        .title {
          font-size: 48px;
          font-weight: 400;
          color: #101828;
          margin: 0 0 0 0;
          line-height: 48px;
          text-shadow: none;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .editNameButton {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: #9ca3af;
          opacity: 0.6;
          transition: opacity 0.2s;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .editNameButton:hover {
          opacity: 1;
        }

        .description {
          font-size: 16px;
          color: #4a5565;
          margin: 0 0 24px 0;
          line-height: 24px;
          letter-spacing: -0.3125px;
        }

        .headerActions {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
        }

        .headerActions .heartButton,
        .headerActions .shareButton,
        .headerActions .addCreatorsButton,
        .headerActions .moreButton {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: white;
          border: 1px solid #e5e7eb;
          color: #6b7280;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .headerActions .heartButton:hover,
        .headerActions .shareButton:hover,
        .headerActions .addCreatorsButton:hover,
        .headerActions .moreButton:hover {
          background: #f9fafb;
          border-color: #d1d5dc;
        }

        .headerActions .heartButton.active {
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
        }

        .headerActions .heartButton.active:hover {
          background: rgba(239, 68, 68, 0.15);
        }

        .headerStats {
          display: flex;
          gap: 32px;
          align-items: center;
        }

        .stat {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0;
          min-width: 120px;
        }

        .statValue {
          font-size: 24px;
          font-weight: 400;
          color: #101828;
          line-height: 32px;
        }

        .statLabel {
          font-size: 14px;
          font-weight: 400;
          color: #4a5565;
          text-transform: capitalize;
          letter-spacing: -0.15px;
          line-height: 20px;
        }

        /* ===== TRACKING ROW ===== */
        .trackingRow {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 17px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          margin-bottom: 24px;
          gap: 24px;
        }

        .trackingInfo {
          display: flex;
          align-items: center;
          gap: 16px;
          flex: 1;
        }

        .trackingBadge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 8px;
          background: #e5e7eb;
          color: #4a5565;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          line-height: 16px;
          white-space: nowrap;
        }

        .trackingBadgeActive {
          background: #d1fae5;
          color: #065f46;
        }

        .trackingBadgePaused {
          background: #fef3c7;
          color: #92400e;
        }

        .activeDot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #10b981;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .trackingInfoItem {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          font-weight: 400;
          color: #4a5565;
          line-height: 20px;
          letter-spacing: -0.15px;
        }

        .trackingIcon {
          flex-shrink: 0;
          color: #6b7280;
        }

        .trackingActions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .manageKeywordsButton {
          padding: 8px 15px;
          background: white;
          border: 1px solid #d1d5dc;
          border-radius: 6px;
          color: #be43ff;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .manageKeywordsButton:hover {
          background: #f9fafb;
          border-color: #be43ff;
        }

        .activateTrackingButton,
        .rescanButton {
          padding: 8px 23px;
          background: #be43ff;
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .activateTrackingButton:hover,
        .rescanButton:hover {
          background: #a536e0;
        }

        .reenableButton {
          padding: 8px 16px;
          background: transparent;
          border: 1px solid #be43ff;
          border-radius: 6px;
          color: #be43ff;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .reenableButton:hover {
          background: #f3e8ff;
        }

        /* ===== TAB NAVIGATION ===== */
        .tabNavigation {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          background: #f8f9fa;
          z-index: 100;
          margin-bottom: 0;
          margin-top: 0;
        }

        .tabNavigationLeft {
          display: flex;
          align-items: center;
          gap: 0;
        }

        .tabNavigationRight {
          display: flex;
          align-items: center;
          gap: 0;
          padding-right: 16px;
        }

        .tab {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          color: #4a5565;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: -0.15px;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
          position: relative;
        }

        .tab:hover:not(.disabledTab) {
          color: #101828;
          background: rgba(152, 16, 250, 0.05);
        }

        .activeTab {
          color: #9810fa;
          border-bottom-color: #9810fa;
        }

        .disabledTab {
          color: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .tabBadge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          background: #9810fa;
          color: white;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
          line-height: 16px;
          margin-left: 6px;
        }

        .sortButtons {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sortButton {
          background: none;
          border: none;
          color: #4a5565;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 6px;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .sortButton:hover {
          color: #101828;
          background: rgba(152, 16, 250, 0.05);
        }

        .sortButton.active {
          color: #9810fa;
          background: rgba(152, 16, 250, 0.1);
        }

        /* ===== CONTENT AREA ===== */
        .contentArea {
          flex: 1;
          background: #f8f9fa;
          overflow: visible;
          padding: 0 24px 32px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .contentAreaInner {
          width: 100%;
          max-width: 1270px;
        }

        .resultsList {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }

        .cardWrapper {
          cursor: grab;
          user-select: none;
          position: relative;
          z-index: 1;
        }

        .cardWrapper:active {
          cursor: grabbing;
        }

        .cardWrapper:hover .card {
          background-color: #ffffff !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(26, 26, 26, 0.2);
        }

        /* ===== CREATOR CARD ===== */
        .card {
          width: 100%;
          min-width: 1270px;
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px 16px 8px 24px;
          margin: 0 0 6px 0;
          transition: all 0.3s ease;
          overflow-x: auto;
          cursor: pointer;
          scroll-behavior: smooth;
        }

        .cardContent {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 24px;
          width: 100%;
          min-width: 1158px;
        }

        .avatarSection {
          width: 96px;
          min-width: 96px;
          max-width: 96px;
          height: auto;
          flex-shrink: 0;
          flex-grow: 0;
        }

        .avatarContainer {
          width: 96px;
          height: 96px;
          position: relative;
          display: block;
        }

        .avatar {
          width: 96px !important;
          height: 96px !important;
          min-width: 96px !important;
          min-height: 96px !important;
          max-width: 96px !important;
          max-height: 96px !important;
          border-radius: 50%;
          border: 2px solid #e5e7eb;
          background-color: #e5e7eb;
          object-fit: cover;
          display: block;
          flex-shrink: 0;
        }

        .profileInfoSection {
          width: 334px;
          min-width: 334px;
          max-width: 334px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 4px;
          flex-shrink: 0;
          flex-grow: 0;
        }

        .nameRow {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          width: 100%;
          height: auto;
        }

        .name {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 18px;
          font-weight: 500;
          line-height: 28px;
          letter-spacing: -0.44px;
          color: #101828;
          flex: 0 1 auto;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          margin: 0;
          padding: 0;
        }

        .verifiedBadge {
          width: 16px;
          height: 16px;
          min-width: 16px;
          min-height: 16px;
          border-radius: 50%;
          background: #2b7fff;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
        }

        .actionButtons {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          margin-left: auto;
          flex-shrink: 0;
        }

        .actionButtons .heartButton,
        .actionButtons .circlePlusButton,
        .actionButtons .trashButton {
          width: 36px;
          height: 36px;
          background: none;
          border: none;
          color: rgba(26, 26, 26, 0.75);
          cursor: pointer;
          border-radius: 4px;
          transition: color 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          position: relative;
        }

        .actionButtons .heartButton:hover:not(:disabled),
        .actionButtons .circlePlusButton:hover:not(:disabled) {
          color: #1a1a1a;
        }

        .actionButtons .heartButton.active {
          color: #ef4444;
        }

        .handleRow {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          width: 100%;
        }

        .handle {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          letter-spacing: -0.15px;
          color: #4a5565;
          margin: 0;
          padding: 0;
        }

        .externalLinkButton {
          background: none;
          border: none;
          color: #4a5565;
          cursor: pointer;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .locationCategoryRow {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 8px;
          width: 100%;
          margin: 2px 0 0 0;
        }

        .locationItem {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 4px;
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 16px;
          color: #6a7282;
        }

        .bullet {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          color: #d1d5dc;
        }

        .categoryTag {
          background: #f3e8ff;
          color: #8200db;
          border-radius: 9999px;
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 16px;
          padding: 2px 8px;
          margin: 0;
          white-space: nowrap;
          display: inline-block;
        }

        .statsAndActionRow {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          width: 100%;
          margin-top: 4px;
        }

        .statsInline {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          gap: 24px;
        }

        .statInline {
          display: flex;
          flex-direction: row;
          align-items: baseline;
          justify-content: flex-start;
          gap: 4px;
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: -0.15px;
        }

        .statInline .statValue {
          font-weight: 500;
          color: #101828;
        }

        .statInline .statLabel {
          font-weight: 400;
          color: #6a7282;
        }

        .viewDetailsButton {
          background: none;
          border: none;
          border-radius: 9999px;
          color: #9810fa;
          cursor: pointer;
          padding: 4px 12px;
          margin: 0;
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 4px;
          max-width: 94px;
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 16px;
          transition: background-color 0.2s;
          white-space: normal;
          flex-shrink: 0;
        }

        .viewDetailsButton:hover {
          background: #f3e8ff;
        }

        .contentSection {
          width: 752px;
          min-width: 752px;
          max-width: 752px;
          flex-shrink: 0;
          flex-grow: 0;
        }

        .contentGrid {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 8px;
          width: 100%;
          min-height: 208px;
          position: relative;
        }

        .contentItem {
          width: 144px;
          height: 208px;
          min-width: 144px;
          min-height: 208px;
          max-width: 144px;
          max-height: 208px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #ffffff;
          overflow: hidden;
          flex-shrink: 0;
        }

        .contentImage {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        /* ===== MENTIONS CONTAINER ===== */
        .mentionsContainer {
          width: 100%;
          padding: 24px 0;
        }

        .mentionsToolbar {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          justify-content: space-between;
          margin-bottom: 16px;
          gap: 16px;
        }

        .mentionsSearchBar {
          flex: 1;
          max-width: 400px;
          min-width: 250px;
        }

        .searchInputWrapper {
          position: relative;
          width: 100%;
        }

        .searchInputIcon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
          color: #9ca3af;
          pointer-events: none;
        }

        .mentionsSearchInput {
          width: 100%;
          padding: 8px 12px 8px 36px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          color: #101828;
          background: #ffffff;
          transition: all 0.2s ease;
        }

        .mentionsSearchInput:focus {
          outline: none;
          border-color: #9810fa;
          box-shadow: 0 0 0 3px rgba(152, 16, 250, 0.1);
        }

        .mentionsSearchInput::placeholder {
          color: #9ca3af;
        }

        .filterButtonWrapper {
          position: relative;
        }

        .filterButton {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #101828;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .filterButton:hover {
          background: #f9fafb;
          border-color: #9810fa;
        }

        .filterIcon {
          width: 18px;
          height: 18px;
          color: #6a7282;
        }

        .filterBadge {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          background: #9810fa;
          color: #ffffff;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 600;
        }

        .filterDropdown {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
          padding: 0;
          z-index: 100;
          min-width: 360px;
          max-width: 400px;
        }

        .filterDropdownHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .filterDropdownTitle {
          font-size: 16px;
          font-weight: 600;
          color: #101828;
        }

        .filterCloseButton {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          color: #6a7282;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .filterCloseButton:hover {
          background: #f9fafb;
          color: #101828;
        }

        .filterSection {
          padding: 20px;
          border-bottom: 1px solid #f3f4f6;
        }

        .filterSection:last-of-type {
          border-bottom: none;
        }

        .filterSectionTitle {
          font-size: 14px;
          font-weight: 600;
          color: #101828;
          margin-bottom: 12px;
        }

        .filterPlatformButtons {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .filterPlatformButton {
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #4a5565;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filterPlatformButton:hover {
          background: #f9fafb;
          border-color: #9810fa;
        }

        .filterPlatformButton.active {
          background: #f3ecff;
          color: #9810fa;
          border-color: #9810fa;
          font-weight: 500;
        }

        .filterTimeframeButtons {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filterTimeframeButton {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #4a5565;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .filterTimeframeButton:hover {
          background: #f9fafb;
          border-color: #9810fa;
        }

        .filterTimeframeButton.active {
          background: #f3ecff;
          color: #9810fa;
          border-color: #9810fa;
          font-weight: 500;
        }

        .filterTimeframeIcon {
          width: 16px;
          height: 16px;
        }

        .filterDateRange {
          display: flex;
          gap: 12px;
          margin-top: 12px;
        }

        .filterDateInputGroup {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }

        .filterDateLabel {
          font-size: 12px;
          font-weight: 500;
          color: #6a7282;
        }

        .filterDateInput {
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          color: #101828;
          background: #ffffff;
          transition: all 0.2s ease;
        }

        .filterDateInput:focus {
          outline: none;
          border-color: #9810fa;
          box-shadow: 0 0 0 3px rgba(152, 16, 250, 0.1);
        }

        .filterActions {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          background: #f9fafb;
          border-radius: 0 0 12px 12px;
        }

        .filterClearButton {
          flex: 1;
          padding: 10px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #6a7282;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filterClearButton:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
          color: #101828;
        }

        .filterApplyButton {
          flex: 1;
          padding: 10px 16px;
          border: none;
          border-radius: 6px;
          background: #9810fa;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filterApplyButton:hover {
          background: #7d0dd1;
        }

        .mentionsPlatformFilters {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .platformFilterButton {
          padding: 6px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #4a5565;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .platformFilterButton:hover {
          background: #f9fafb;
          border-color: #9810fa;
        }

        .platformFilterButton.active {
          background: #9810fa;
          color: #ffffff;
          border-color: #9810fa;
        }

        .timeframeFilterGroup {
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 0 0 auto;
        }

        .timeframeLabel {
          font-size: 14px;
          font-weight: 500;
          color: #101828;
          white-space: nowrap;
        }

        .timeframeButtons {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .timeframeButton {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          background: #ffffff;
          color: #4a5565;
          font-size: 14px;
          font-weight: 400;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .timeframeButton:hover {
          background: #f9fafb;
          border-color: #9810fa;
        }

        .timeframeButton.active {
          background: #9810fa;
          color: #ffffff;
          border-color: #9810fa;
        }

        .timeframeIcon {
          width: 16px;
          height: 16px;
        }

        .dateRangePicker {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          padding: 16px;
          z-index: 100;
          min-width: 400px;
        }

        .dateRangeInputs {
          display: flex;
          align-items: flex-end;
          gap: 12px;
        }

        .dateInputGroup {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;
        }

        .dateLabel {
          font-size: 12px;
          font-weight: 500;
          color: #6a7282;
        }

        .dateInput {
          padding: 8px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          font-size: 14px;
          color: #101828;
          background: #ffffff;
          transition: all 0.2s ease;
        }

        .dateInput:focus {
          outline: none;
          border-color: #9810fa;
          box-shadow: 0 0 0 3px rgba(152, 16, 250, 0.1);
        }

        .applyDateButton {
          padding: 8px 20px;
          background: #9810fa;
          color: #ffffff;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .applyDateButton:hover {
          background: #7d0dd1;
        }

        .mentionsTableWrapper {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .mentionsTable {
          width: 100%;
          border-collapse: collapse;
        }

        .mentionsTableHeader {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .mentionsHeaderCell {
          padding: 12px 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 500;
          color: #6a7282;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .mentionsTableRow {
          border-bottom: 1px solid #e5e7eb;
          transition: background 0.2s ease;
        }

        .mentionsTableRow:last-child {
          border-bottom: none;
        }

        .mentionsTableRow:hover {
          background: #f9fafb;
        }

        .mentionsTableCell {
          padding: 12px 16px;
          vertical-align: middle;
        }

        .mentionsIndexCell {
          text-align: center;
          font-size: 14px;
          font-weight: 500;
          color: #6a7282;
        }

        .mentionsCreatorCell {
          padding: 12px 16px;
        }

        .mentionsCreatorContent {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mentionsCreatorAvatarTable {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e5e7eb;
          object-fit: cover;
          flex-shrink: 0;
        }

        .mentionsCreatorInfo {
          flex: 1;
          min-width: 0;
        }

        .mentionsCreatorNameTable {
          font-size: 14px;
          font-weight: 500;
          color: #101828;
          line-height: 20px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .mentionsCreatorHandleTable {
          font-size: 12px;
          font-weight: 400;
          color: #6a7282;
          line-height: 16px;
        }

        .verifiedBadge {
          width: 16px;
          height: 16px;
          min-width: 16px;
          min-height: 16px;
          border-radius: 50%;
          background: #2b7fff;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
        }

        .mentionsPostCell {
          max-width: 500px;
        }

        .mentionsPostContent {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .mentionsPostThumbnail {
          width: 60px;
          height: 60px;
          border-radius: 6px;
          overflow: hidden;
          flex-shrink: 0;
          background: #f3f4f6;
        }

        .mentionsPostImage {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .mentionsPostCaption {
          flex: 1;
          font-size: 14px;
          font-weight: 400;
          color: #364153;
          line-height: 20px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .mentionsKeywordHighlight {
          background: #fef3c7;
          color: #92400e;
          padding: 2px 4px;
          border-radius: 3px;
          font-weight: 500;
        }

        .mentionsPlatformCell {
          text-align: left;
        }

        .mentionsPlatformBadgeTable {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 400;
          color: #6a7282;
        }

        .mentionsEngagementCell {
          padding: 12px 16px;
        }

        .mentionsEngagementStats {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .mentionsEngagementItem {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 400;
          color: #6a7282;
        }

        .mentionsEngagementItem svg {
          color: #9ca3af;
          flex-shrink: 0;
        }

        .mentionsTimeCell {
          font-size: 12px;
          font-weight: 400;
          color: #6a7282;
          text-align: right;
        }

        .bullet {
          font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 16px;
          font-weight: 400;
          line-height: 24px;
          color: #d1d5dc;
        }

        /* ===== LEADERBOARD CONTAINER ===== */
        .leaderboardContainer {
          width: 100%;
          padding: 24px 0;
        }

        .leaderboardTableWrapper {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }

        .leaderboardTable {
          width: 100%;
          border-collapse: collapse;
        }

        .leaderboardTableHeader {
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
        }

        .leaderboardHeaderCell {
          padding: 12px 16px;
          text-align: left;
          font-size: 11px;
          font-weight: 500;
          color: #6a7282;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          line-height: 16px;
        }

        .leaderboardTableRow {
          border-bottom: 1px solid #e5e7eb;
          transition: background 0.2s ease;
        }

        .leaderboardTableRow:last-child {
          border-bottom: none;
        }

        .leaderboardTableRow:hover {
          background: #f9fafb;
        }

        .leaderboardTableCell {
          padding: 14px 16px;
          vertical-align: middle;
          font-size: 14px;
          font-weight: 400;
          color: #101828;
        }

        .leaderboardRankCell {
          text-align: center;
          font-weight: 400;
          color: #6a7282;
        }

        .leaderboardCreatorCellTable {
          padding: 14px 16px;
        }

        .leaderboardCreatorContent {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .leaderboardAvatarTable {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #e5e7eb;
          object-fit: cover;
          flex-shrink: 0;
        }

        .leaderboardCreatorInfoTable {
          flex: 1;
          min-width: 0;
        }

        .leaderboardCreatorNameTable {
          font-size: 14px;
          font-weight: 400;
          color: #101828;
          line-height: 20px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .leaderboardCreatorHandleTable {
          font-size: 12px;
          font-weight: 400;
          color: #6a7282;
          line-height: 16px;
        }

        .verifiedBadge {
          width: 16px;
          height: 16px;
          min-width: 16px;
          min-height: 16px;
          border-radius: 50%;
          background: #2b7fff;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          font-size: 10px;
        }

        .leaderboardChannelCell {
          text-align: left;
        }

        .leaderboardChannelBadge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          background: #db2777;
          color: #ffffff;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .leaderboardFollowersCell {
          font-weight: 400;
          color: #101828;
        }

        .leaderboardMentionsCell {
          padding: 14px 16px;
        }

        .leaderboardMentionsContent {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .leaderboardMentionsLink {
          font-size: 14px;
          font-weight: 400;
          color: #2563eb;
          cursor: pointer;
          line-height: 20px;
        }

        .leaderboardMentionsLink:hover {
          text-decoration: underline;
        }

        .leaderboardMentionsSubtext {
          font-size: 12px;
          font-weight: 400;
          color: #9ca3af;
          line-height: 16px;
        }

        .leaderboardMetricCell {
          font-weight: 400;
          color: #101828;
          text-align: left;
        }

        .leaderboardLatestMentionCell {
          font-size: 14px;
          font-weight: 400;
          color: #6a7282;
        }

        .leaderboardActionsCell {
          text-align: center;
        }

        .leaderboardActionsButton {
          background: none;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .leaderboardActionsButton:hover {
          background: #f3f4f6;
          color: #4a5565;
        }
      `}</style>
    </div>
  );
}
